import Card from 'components/card';
import Pagination from 'components/pagination/Pagination';
import { useEffect, useState } from 'react';
import { MdEuro, MdWarning, MdHourglassEmpty } from 'react-icons/md';
import { getBiUnpaidInvoices } from 'interfaces/bi';
import { formatCurrency, formatNumber } from './helpers';

type StatusFilter = '' | 'late' | 'pending';

/**
 * KPI #4 — Unpaid / pending invoices.
 *
 * Headline stats (overdue / pending outstanding) come from the pre-aggregated
 * summary; the paginated detail list is filterable by status. Reads from
 * `/bi/invoices/unpaid`.
 */
function UnpaidInvoices() {
    const [rows, setRows] = useState<any[]>([]);
    const [meta, setMeta] = useState<any>(null);
    const [summary, setSummary] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [status, setStatus] = useState<StatusFilter>('');
    const [loading, setLoading] = useState(true);

    const fetchData = async (p = page, l = limit, s = status) => {
        setLoading(true);
        const res = await getBiUnpaidInvoices({
            status: s || undefined,
            page: p,
            limit: l,
        });
        setRows(res?.data ?? []);
        setMeta(res?.meta ?? null);
        setSummary(res?.summary ?? null);
        setPage(p);
        setLimit(l);
        setLoading(false);
    };

    useEffect(() => {
        fetchData(1, limit, status);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const fmtDate = (d: string | null) =>
        d ? new Date(d).toLocaleDateString('fr-FR') : '-';

    return (
        <Card extra="p-5 border h-full">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h4 className="text-lg font-bold text-navy-700">
                        Impayés & factures en attente
                    </h4>
                    <p className="text-sm text-gray-500">Montants dus par statut</p>
                </div>
                <div className="flex gap-3 text-sm">
                    {[
                        { label: 'Tous', value: '' },
                        { label: 'En retard', value: 'late' },
                        { label: 'En attente', value: 'pending' },
                    ].map((s) => (
                        <button
                            key={s.value}
                            onClick={() => setStatus(s.value as StatusFilter)}
                            className={`cursor-pointer ${
                                status === s.value
                                    ? 'font-bold text-toola-500 underline'
                                    : 'text-gray-500'
                            }`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-2xl text-red-600">
                        <MdWarning />
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-600">En retard</p>
                        <h3 className="text-lg font-bold text-navy-700">
                            {formatCurrency(summary?.totalLate)}
                        </h3>
                        <p className="text-[11px] text-gray-400">
                            {formatNumber(summary?.countLate)} facture(s)
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-orange-100 text-2xl text-orange-500">
                        <MdHourglassEmpty />
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-600">En attente</p>
                        <h3 className="text-lg font-bold text-navy-700">
                            {formatCurrency(summary?.totalPending)}
                        </h3>
                        <p className="text-[11px] text-gray-400">
                            {formatNumber(summary?.countPending)} facture(s)
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-lightPrimary text-2xl text-toola-500">
                        <MdEuro />
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-600">Total dû</p>
                        <h3 className="text-lg font-bold text-navy-700">
                            {formatCurrency(summary?.totalOutstanding)}
                        </h3>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                    <thead>
                        <tr className="border-b border-gray-200 text-left">
                            <th className="py-2 text-xs font-bold text-gray-600">Facture</th>
                            <th className="py-2 text-xs font-bold text-gray-600">Client</th>
                            <th className="py-2 text-xs font-bold text-gray-600">Échéance</th>
                            <th className="py-2 text-xs font-bold text-gray-600">Reste dû</th>
                            <th className="py-2 text-xs font-bold text-gray-600">Statut</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-gray-400">
                                    Chargement...
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-gray-400">
                                    Aucune facture impayée.
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => (
                                <tr
                                    key={r._id}
                                    className="border-b border-gray-100 hover:bg-gray-50"
                                >
                                    <td className="py-2 pr-2 text-sm font-medium text-navy-700">
                                        {r.invoiceNumber || '—'}
                                    </td>
                                    <td className="py-2 text-sm text-navy-700">
                                        {r.company || '—'}
                                    </td>
                                    <td className="py-2 text-sm text-navy-700">
                                        {fmtDate(r.dueDate)}
                                    </td>
                                    <td className="py-2 text-sm font-semibold text-navy-700">
                                        {formatCurrency(r.leftToPay)}
                                    </td>
                                    <td className="py-2">
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                r.status === 'late'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-orange-100 text-orange-700'
                                            }`}
                                        >
                                            {r.status === 'late' ? 'En retard' : 'En attente'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination
                extra="mt-3"
                text={false}
                type="factures"
                maxItems={meta ? meta.total : 0}
                itemsPerPage={[10, 25, 50]}
                page={page}
                defaultItemsPerPage={limit}
                onChange={(p: number, l: number) => fetchData(p, l, status)}
            />
        </Card>
    );
}

export default UnpaidInvoices;
